import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../App';
import { toast } from 'sonner';
import api from '../utils/api';
import { Lock, Eye, EyeOff, CheckCircle, User, Bell, Sun, Moon, Monitor } from 'lucide-react';
import { subscribeToPush, isPushSubscribed, isPushConfigured } from '../utils/push';

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushConfigured, setPushConfigured] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => setProfile(r.data)).catch(() => {});
    isPushSubscribed().then(setPushEnabled);
    isPushConfigured().then(setPushConfigured);
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const THEMES = [
    { id: 'light', label: 'Light', Icon: Sun },
    { id: 'dark', label: 'Dark', Icon: Moon },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Settings</h1>
        <p className="text-slate-500 mt-0.5 text-xs">Manage your account settings</p>
      </div>

      {/* Appearance */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="appearance-settings">
        <div className="flex items-center gap-2 mb-3">
          <Sun className="w-4 h-4 text-slate-600" />
          <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Appearance</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">Choose how Jump Smash looks on your device.</p>
        <div className="flex gap-2">
          {THEMES.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTheme(id)}
              data-testid={`theme-${id}`}
              className={`flex-1 flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all ${
                theme === id
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'
              }`}
            >
              <Icon size={18} />
              <span className="text-xs font-semibold">{label}</span>
              {theme === id && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
            </button>
          ))}
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <User className="w-4 h-4 text-slate-600" />
          <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Account</h2>
        </div>
        <div className="space-y-2 text-sm">
          {[
            { label: 'Username', value: user?.username },
            { label: 'Full Name', value: profile?.name },
            { label: 'Email', value: profile?.email },
            { label: 'Phone', value: profile?.phone },
            { label: 'Role', value: user?.role, badge: true },
          ].filter(r => r.value).map(r => (
            <div key={r.label} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
              <span className="text-slate-500 text-xs">{r.label}</span>
              {r.badge ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium border border-green-200">{r.value}</span>
              ) : (
                <span className="font-medium text-slate-900 text-xs">{r.value}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Push Notifications */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="push-settings">
        <div className="flex items-center gap-2 mb-2">
          <Bell className="w-4 h-4 text-slate-600" />
          <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Push Notifications</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">Get notified about announcements, game invites, and score updates.</p>
        {pushEnabled ? (
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
            <CheckCircle size={14} />
            <span className="font-medium">Push notifications enabled</span>
          </div>
        ) : pushConfigured === false ? (
          <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
            Not configured on this server. Contact your administrator.
          </div>
        ) : (
          <button
            onClick={async () => {
              setPushLoading(true);
              const result = await subscribeToPush();
              setPushLoading(false);
              if (result.ok) { setPushEnabled(true); toast.success('Push notifications enabled!'); }
              else if (result.reason === 'permission_denied') toast.error('Browser permission denied. Allow notifications in browser settings.');
              else if (result.reason === 'unsupported') toast.error('Push not supported in this browser.');
              else if (result.reason === 'not_configured') { toast.error('Push not configured on this server.'); setPushConfigured(false); }
              else toast.error('Failed to enable push notifications.');
            }}
            disabled={pushLoading || pushConfigured === null}
            className="h-9 px-4 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all"
            data-testid="enable-push-btn"
          >
            {pushLoading ? 'Enabling...' : pushConfigured === null ? 'Checking...' : 'Enable Push Notifications'}
          </button>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-4 h-4 text-slate-600" />
          <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Change Password</h2>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-3">
          {[
            { label: 'Current Password', val: oldPassword, set: setOldPassword, show: showOld, toggleShow: () => setShowOld(v => !v), testid: 'old-password-input' },
            { label: 'New Password', val: newPassword, set: setNewPassword, show: showNew, toggleShow: () => setShowNew(v => !v), testid: 'new-password-input', minLength: 6 },
          ].map(f => (
            <div key={f.label}>
              <label className="block text-xs font-medium text-slate-700 mb-1">{f.label}</label>
              <div className="relative">
                <input
                  type={f.show ? 'text' : 'password'}
                  value={f.val}
                  onChange={e => f.set(e.target.value)}
                  className="w-full h-9 px-3 pr-9 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder={f.label}
                  required minLength={f.minLength}
                  data-testid={f.testid}
                />
                <button type="button" onClick={f.toggleShow} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {f.show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Confirm new password"
              required
              data-testid="confirm-password-input"
            />
            {confirmPassword && newPassword === confirmPassword && (
              <p className="text-[10px] text-green-600 mt-1 flex items-center gap-1"><CheckCircle size={10} /> Passwords match</p>
            )}
          </div>
          <button
            type="submit"
            disabled={loading || !oldPassword || !newPassword || newPassword !== confirmPassword}
            className="w-full h-9 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white font-semibold rounded-lg transition-all text-sm"
            data-testid="change-password-btn"
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
